import { doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

// Note: stripePromise supprimé car nous utilisons Stripe Checkout (redirection)
// et non Stripe Elements (formulaire intégré)

// Fonction utilitaire pour obtenir Stripe (si besoin pour Elements plus tard)
export const getStripe = async () => {
  const { loadStripe } = await import('@stripe/stripe-js');
  return loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
};

// Créer une session de paiement pour le Pass Codeur (avec support PayPal)
export const createCheckoutSession = async ({ userId, amount, currency = 'eur', userEmail }) => {
  try {
    if (import.meta.env.MODE !== 'production') {
      console.log('Création d\'une session de paiement Pass Codeur avec PayPal pour:', { userId, amount, currency, userEmail });
    }
    
    // Appel à votre backend pour créer une vraie session Stripe
    const response = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        amount,
        currency,
        userEmail
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    
    const session = await response.json();
    if (import.meta.env.MODE !== 'production') {
      console.log('Session Stripe créée:', session.sessionId);
    }
    
    return {
      url: session.url,
      sessionId: session.sessionId
    };
    
  } catch (error) {
    console.error("Erreur lors de la création de la session de paiement:", error);
    throw error;
  }
};

// Traiter l'achat du Pass Codeur et l'enregistrer dans Firebase
export const processPurchaseSuccess = async (userId, paymentDetails) => {
  try {
    if (import.meta.env.MODE !== 'production') {
      console.log('[FIREBASE] Traitement de l\'achat du Pass Codeur:', { userId, paymentDetails });
    }
    
    // Enregistrer l'achat dans la collection users
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    const purchaseData = {
      hasPassCodeur: true,
      passCodeurPurchasedAt: new Date().toISOString()
    };
    
    if (userDoc.exists()) {
      // Mettre à jour le document utilisateur existant
      await updateDoc(userDocRef, purchaseData);
    } else {
      // Créer un nouveau document utilisateur
      await setDoc(userDocRef, {
        ...purchaseData,
        createdAt: new Date().toISOString()
      });
    }
    
    if (import.meta.env.MODE !== 'production') {
      console.log('Pass Codeur enregistré avec succès dans Firebase');
    }
    return { success: true };
    
  } catch (error) {
    console.error("Erreur lors de l'enregistrement de l'achat:", error);
    throw error;
  }
};

// Vérifier si un utilisateur a acheté le Pass Codeur
export const checkPassCodeurStatus = async (userId) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    if (import.meta.env.MODE !== 'production') {
      console.log('[FIREBASE] Vérification du champ hasPassCodeur pour l\'utilisateur:', userId);
    }
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        hasPassCodeur: userData.hasPassCodeur || false,
        purchasedAt: userData.passCodeurPurchasedAt || null
      };
    }
    
    return { hasPassCodeur: false, purchasedAt: null };
  } catch (error) {
    console.error("Erreur lors de la vérification du Pass Codeur:", error);
    throw error;
  }
};



// Supprimer le Pass Codeur d'un utilisateur (fonction admin uniquement)
export const removePassCodeur = async (userId) => {
  try {
    if (import.meta.env.MODE !== 'production') {
      console.log('[FIREBASE] Suppression du Pass Codeur pour l\'utilisateur:', userId);
    }
    
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      // Supprimer les champs liés au Pass Codeur
      await updateDoc(userDocRef, {
        hasPassCodeur: false,
        passCodeurPurchasedAt: null,
        passCodeurPaymentDetails: null // Supprimer aussi les anciens détails de paiement
      });
      
      if (import.meta.env.MODE !== 'production') {
        console.log('Pass Codeur supprimé avec succès pour l\'utilisateur:', userId);
      }
      return { success: true };
    } else {
      throw new Error('Utilisateur non trouvé');
    }
  } catch (error) {
    console.error("Erreur lors de la suppression du Pass Codeur:", error);
    throw error;
  }
};

// Vérifier le statut d'une session de paiement
export const verifyCheckoutSession = async (sessionId) => {
  try {
    if (import.meta.env.MODE !== 'production') {
      console.log('Vérification du statut de la session:', sessionId);
    }
    
    const response = await fetch(`/api/checkout-session/${sessionId}`);
    
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    
    const sessionData = await response.json();
    if (import.meta.env.MODE !== 'production') {
      console.log('Statut de la session:', sessionData);
    }
    
    // Si le paiement est réussi, traiter l'achat
    if (sessionData.status === 'paid' && sessionData.metadata?.userId) {
      // Déterminer la méthode de paiement utilisée
      const paymentMethod = sessionData.payment_method_types?.includes('paypal') ? 'paypal' : 'card';
      
      await processPurchaseSuccess(sessionData.metadata.userId, {
        sessionId,
        amount: sessionData.amount_total,
        currency: sessionData.currency,
        paymentMethod,
        paymentMethodTypes: sessionData.payment_method_types,
        customerEmail: sessionData.customer_email
      });
    }
    
    return sessionData;
    
  } catch (error) {
    console.error("Erreur lors de la vérification de la session:", error);
    throw error;
  }
};

// Fonction admin pour donner le Pass Codeur directement (sans paiement)
export const grantPassCodeurAdmin = async (userId) => {
  try {
    if (import.meta.env.MODE !== 'production') {
      console.log('[ADMIN] Attribution directe du Pass Codeur pour l\'utilisateur:', userId);
    }
    
    await processPurchaseSuccess(userId, {
      sessionId: `admin_grant_${Date.now()}`,
      amount: 0,
      currency: 'eur',
      paymentMethod: 'admin_grant',
      adminAction: true
    });
    
    if (import.meta.env.MODE !== 'production') {
      console.log('[ADMIN] Pass Codeur accordé avec succès');
    }
    return { success: true };
    
  } catch (error) {
    console.error("Erreur lors de l'attribution du Pass Codeur:", error);
    throw error;
  }
};

// Note: Intégration Stripe complète avec vraies clés de test configurées. 